import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { useGoogleAuth } from '../hooks/api/auth/useGoogleAuth';
import { hasValidSession } from '../hooks/api';
import { getPostLoginPath } from '../utils/postLoginRedirect';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute guarda en `location.state.from` la ruta que se pidió sin sesión: después del
  // login se vuelve ahí (ej. el link de una notificación), en vez de caer siempre en el inicio.
  const postLoginPath = getPostLoginPath(location.state);
  const { authenticateWithApiCore, isAuthenticated } = useAuth();
  const { clearPsychologist } = usePsychologistContext();
  const {
    signInWithGoogle,
    isLoading: googleLoading,
    error: googleError,
    clearError: clearGoogleError
  } = useGoogleAuth();

  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isPopup] = useState(() => !!window.opener);

  // If running inside the OAuth popup, send the hash to the main window and close
  useEffect(() => {
    if (!isPopup) return;
    window.opener.postMessage(
      { type: 'psi-oauth-callback', hash: window.location.hash || '' },
      window.location.origin
    );
    const timeout = setTimeout(() => { try { window.close(); } catch (_) { /* empty */ } }, 500);
    return () => clearTimeout(timeout);
  }, [isPopup]);

  useEffect(() => {
    if (isPopup) return;
    if (hasValidSession()) {
      navigate(postLoginPath, { replace: true });
    }
  }, [navigate, isPopup, postLoginPath]);

  useEffect(() => {
    if (isPopup) return;
    if (isAuthenticated && hasValidSession()) {
      navigate(postLoginPath, { replace: true });
    }
  }, [isAuthenticated, navigate, isPopup, postLoginPath]);

  const handleGoogleLogin = useCallback(async () => {
    setAuthError(null);
    clearGoogleError();
    setIsAuthenticating(true);

    try {
      // Limpiar el contexto del psicólogo antes de iniciar nueva sesión
      clearPsychologist();
      
      // 1. Autenticar con Google (solo para obtener el email verificado)
      const userData = await signInWithGoogle();
      
      if (!userData || !userData.email) {
        throw new Error('No se pudo obtener información de Google');
      }

      if (!userData.email.endsWith('@psimammoliti.com')) {
        throw new Error('Solo se permite el ingreso con cuentas @psimammoliti.com');
      }

      // 2. Obtener el access_token de Google guardado por useGoogleAuth
      const googleAccessToken = localStorage.getItem('google_access_token');
      if (!googleAccessToken) {
        throw new Error('No se pudo obtener el token de Google');
      }

      // 3. Autenticar con API Core enviando email + token de Google para validación en backend
      const apiCoreSuccess = await authenticateWithApiCore(userData.email, googleAccessToken);

      if (!apiCoreSuccess) {
        throw new Error('No se pudo autenticar con el servidor');
      }

      // Restaurar google_user después de que clearSessionData lo borró durante la autenticación
      // (necesario para mostrar la foto de perfil de Google en el panel)
      try { localStorage.setItem('google_user', JSON.stringify(userData)); } catch { /* noop */ }

      // 4. Navegar a donde iba (o al panel del psicólogo)
      navigate(postLoginPath);
      
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error durante la autenticación';
      setAuthError(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  }, [signInWithGoogle, authenticateWithApiCore, navigate, postLoginPath, clearGoogleError, clearPsychologist]);

  const isLoading = googleLoading || isAuthenticating;
  const error = googleError || authError;

  // Don't render anything inside the popup — it'll close itself
  if (isPopup) return null;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo-container">
          <div className="login-logo-circle">
            <img 
              src="/logoPsiMammoliti.svg" 
              alt="Psi Mammoliti Logo" 
              className="login-logo-image"
            />
          </div>
        </div>
        
        <h1 className="login-title">Ingresá a Psi Mammoliti</h1>
        <p className="login-subtitle">Autogestiona tus pacientes con un clic</p>
        
        {error && (
          <div className="login-error">
            <p>❌ {error}</p>
            <button 
              onClick={() => {
                setAuthError(null);
                clearGoogleError();
              }} 
              className="error-dismiss"
            >
              ×
            </button>
          </div>
        )}
        
        <button 
          className={`google-signin-btn ${isLoading ? 'loading' : ''}`}
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="google-loading-spinner"></div>
              {googleLoading 
                ? 'Conectando con Google...' 
                : 'Autenticando con servidor...'
              }
            </>
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Ingresar con Google
            </>
          )}
        </button>
        <div style={{marginTop: "10px"}}></div>
        
        {
          process.env.REACT_APP_ENV !== 'production' && ( 
            <>
              <button
                className={`google-signin-btn ${isLoading ? 'loading' : ''}`}
                onClick={async () => {
                  setIsAuthenticating(true)
                  try {
                    const token = localStorage.getItem('google_access_token');
                    if (!token) {
                      setAuthError('Primero logueate con Google para obtener el token');
                      setIsAuthenticating(false);
                      return;
                    }
                    await authenticateWithApiCore("test.i@psimammoliti.com", token)
                    navigate('/');
                  } catch (e) {
                    setAuthError(e instanceof Error ? e.message : 'Error de autenticación');
                  }
                  setIsAuthenticating(false)
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="google-loading-spinner"></div>
                    Autenticando con servidor...
                  </>
                ) : (
                  <>
                    Ingresar con Maga
                  </>
                )}
              </button>
            </>
          )}
        
      </div>
    </div>
  );
};
